<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form ref="depotForm" @submit="addDepot" :validation-schema="depotSchema">
        <div class="row">
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date de dépôt <span class="text-danger">*</span>
              </label>
              <Field name="dateDepot" type="date" :value="getCurrentDate()"
                class="form-control shadow-none fs-md-15 text-black" />
              <ErrorMessage name="dateDepot" class="text-danger" />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black mb-10">
                Compte <span class="text-danger">*</span>
              </label>
              <Field name="compte" v-slot="{ field }">
                <Multiselect v-model="field.value" v-bind="field" :options="compteOptions" :preserve-search="true"
                  :multiple="false" :searchable="true" placeholder="Sélectionner un compte" label="label"
                  track-by="label" />
              </Field>
              <ErrorMessage name="compte" class="text-danger" />
            </div>
          </div>

          <!-- Mode de règlement -->
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black mb-10">
                Mode de règlement <span class="text-danger">*</span>
              </label>
              <Field name="modeReglement" v-model="modeReglement" type="text" v-slot="{ field }">
                <Multiselect v-model="field.value" v-bind="field" :options="modeReglementOptions"
                  :preserve-search="true" :multiple="false" :searchable="true"
                  placeholder="Sélectionner un mode de règlement" label="label" track-by="label"
                  @select="onModeReglementSelect" />
              </Field>
              <ErrorMessage name="modeReglement" class="text-danger" />
            </div>
          </div>

          <!-- Compte (affiché pour Virement Bancaire et Mobile Money) -->
          <div class="col-md-4 mb-3" v-if="showCompteField">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black mb-10">
                Compte <span class="text-danger">*</span>
              </label>
              <Field name="compteEntreprise" v-model="compte" type="text" v-slot="{ field }">
                <Multiselect v-model="field.value" v-bind="field" :options="compteEntrepriseOptions"
                  :preserve-search="true" :multiple="false" :searchable="true" placeholder="Sélectionner un compte"
                  label="label" track-by="label" />
              </Field>
              <ErrorMessage name="compteEntreprise" class="text-danger" />
            </div>
          </div>

          <!-- Banque (affiché pour Chèques) -->
          <div class="col-md-4 mb-3" v-if="showBanqueField">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black mb-10">
                Banque <span class="text-danger">*</span>
              </label>
              <Field name="banque" v-model="banque" type="text" v-slot="{ field }">
                <Multiselect v-model="field.value" v-bind="field" :options="banqueOptions" :preserve-search="true"
                  :multiple="false" :searchable="true" placeholder="Sélectionner une banque" label="label"
                  track-by="label" />
              </Field>
              <ErrorMessage name="banque" class="text-danger" />
            </div>
          </div>

          <!-- Numéro de chèque (affiché pour Chèques) -->
          <div class="col-md-4 mb-3" v-if="showNumCompteField">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Numéro de chèque <span class="text-danger">*</span>
              </label>
              <Field type="text" name="numeroCheque" v-model="numeroCheque"
                class="form-control shadow-none fs-md-15 text-black" />
              <ErrorMessage name="numeroCheque" class="text-danger" />
            </div>
          </div>

          <!-- Date de virement (affiché pour Virement Bancaire) -->
          <div class="col-md-4 mb-3" v-if="showDateVirementField">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date du virement <span class="text-danger">*</span>
              </label>
              <Field name="dateVirement" type="datetime-local" :value="getCurrentDateTime()"
                class="form-control shadow-none fs-md-15 text-black" />
              <ErrorMessage name="dateVirement" class="text-danger" />
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Montant <span class="text-danger">*</span>
              </label>

              <Field name="montant" v-slot="{ field }">
                <input type="text" v-bind="field" v-format-montant 
                  class="form-control shadow-none fs-md-15 text-black" placeholder="Entrer le montant" />
              </Field>
              <ErrorMessage name="montant" class="text-danger" />
            </div>
          </div>
          <div class="col-md-12 mb-3">
            <div class="form-group mb-3 mb-sm-3 mb-md-3">
              <label class="d-block text-black fw-semibold mb-1">
                Motif<span class="text-danger">*</span>
              </label>
              <Field name="motif" as="textarea" cols="15" rows="3" placeholder="Entrer les autres informations"
                v-slot="{ field }" class="form-control shadow-none rounded-0 text-black">
                <textarea class="form-control shadow-none rounded-0 text-black" v-model="field.value"></textarea>
              </Field>
              <ErrorMessage name="motif" class="text-danger" />
            </div>
          </div>

          <div class="col-md-12 mt-3">
            <div class="d-flex justify-content-start mb-4">
              <button class="btn btn-success me-3" type="submit">
                Enregistrer un dépôt
              </button>
              <router-link to="/depots/liste-depots" class="btn btn-danger">
                <i class="fa fa-trash-o lh-1 me-1 position-relative top-2"></i>
                <span class="position-relative">Annuler</span>
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import { error, success } from '@/utils/utils';
import { useRouter } from "vue-router";
import ApiService from '@/services/ApiService';
import { Depot } from '@/models/Depot';
import * as Yup from 'yup';
import Multiselect from '@vueform/multiselect';

export default defineComponent({
  name: "AddDepot",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  setup() {
    // Dynamic validation schema based on payment method
    const depotSchema = computed(() => {
      const schema: any = {
        montant: Yup.number().required('Le montant déposé est obligatoire').typeError('Le montant doit être un nombre'),
        dateDepot: Yup.date().required('La date de dépôt est obligatoire'),
        modeReglement: Yup.number().required('Le mode de règlement est obligatoire').typeError('Veuillez sélectionner un mode de règlement'),
        compte: Yup.number().required('Le compte est obligatoire').typeError('Veuillez sélectionner un compte'),
        motif: Yup.string().required('le motif est obligatoire'),
      };

      if (showCompteField.value) {
        schema.compteEntreprise = Yup.number().required('Le compte est obligatoire').typeError('Veuillez sélectionner un compte');
      }
      if (showBanqueField.value) {
        schema.banque = Yup.number().required('La banque est obligatoire').typeError('Veuillez sélectionner une banque');
      }
      if (showNumCompteField.value) {
        schema.numeroCheque = Yup.string().required('Le numéro de chèque est obligatoire');
      }
      if (showDateVirementField.value) {
        schema.dateVirement = Yup.string().required('La date du virement est obligatoire');
      }

      return Yup.object().shape(schema);
    });

    const depotForm = ref<Depot | null>(null);
    const router = useRouter();
    const modeReglementOptions = ref([]);
    const compteOptions = ref<any[]>([]);
    const compteEntrepriseOptions = ref<any[]>([]);
    const banqueOptions = ref<any[]>([]);

    // Mapping des types de compte
    const typeCompteLabels: { [key: string]: string } = {
      '1': 'Compte Comptant',
      '2': 'Compte Créance',
      '3': 'Compte Dépot-Vente',
    };

    const compte = ref(null);
    const banque = ref(null);
    const numeroCheque = ref<string>('');
    const modeReglement = ref(null);
    const selectedModeReglementLabel = ref<string | null>(null);

    // Variables pour contrôler l'affichage des champs
    const showCompteField = ref(false);
    const showBanqueField = ref(false);
    const showNumCompteField = ref(false);
    const showDateVirementField = ref(false);

    function getCurrentDateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    // Charger les modes de règlement
    const getAllModesReglement = async () => {
      try {
        const response = await ApiService.get('/modes-reglement');
        const modesData = response.data.data;
        modeReglementOptions.value = modesData.map((mode: any) => ({
          value: mode.id,
          label: mode.libelle,
        }));
      } catch (err) {
        error(err.message || 'Erreur lors de la récupération des modes de règlement');
      }
    };

    // Charger les comptes entreprise
    const getAllCompteEntreprise = async () => {
      try {
        const response = await ApiService.get('/comptes-entreprise');
        const comptesData = response.data.data;
        compteEntrepriseOptions.value = comptesData.map((compte: any) => ({
          value: compte.id,
          label: `${compte.typeCompte}${
        compte.typeCompte === 'Compte Bancaire' 
          ? '-' + (compte.banque?.sigle || 'N/A') 
          : ''
      }-(${compte.telephoneMoMo || compte.numeroCompteEnt})`,
        }));
      } catch (err) {
        error(err.message);
      }
    };

    // Charger les banques
    const getAllBanques = async () => {
      try {
        const response = await ApiService.get('/banques');
        const banquesData = response.data.data;
        banqueOptions.value = banquesData.map((banque: any) => ({
          value: banque.id,
          label: banque.denominationBanque,
        }));
      } catch (err) {
        error(err.message);
      }
    };

    // Mettre à jour les champs affichés lors de la sélection du mode de règlement
    const onModeReglementSelect = (value: number) => {
      const selectedMode = modeReglementOptions.value.find((option: any) => option.value === value);
      selectedModeReglementLabel.value = selectedMode ? selectedMode.label : null;
      resetFields();
      if (selectedModeReglementLabel.value) {
        const modeLabel = selectedModeReglementLabel.value.toUpperCase();
        if (modeLabel === 'ESPECES') {
          showCompteField.value = false;
          showBanqueField.value = false;
          showNumCompteField.value = false;
          showDateVirementField.value = false;
        } else if (modeLabel === 'CHEQUES') {
          showCompteField.value = false;
          showBanqueField.value = true;
          showNumCompteField.value = true;
          showDateVirementField.value = false;
        } else if (modeLabel === 'VIREMENT') {
          showCompteField.value = true;
          showBanqueField.value = false;
          showNumCompteField.value = false;
          showDateVirementField.value = true;
        } else if (modeLabel === 'MOBILEMONEY') {
          showCompteField.value = true;
          showBanqueField.value = false;
          showNumCompteField.value = false;
          showDateVirementField.value = false;
        }
      }
    };

    // Réinitialiser les champs
    const resetFields = () => {
      compte.value = null;
      banque.value = null;
      numeroCheque.value = '';
    };

    // Charger les comptes
    const getAllComptes = async () => {
      try {
        const response = await ApiService.get('/all/comptes');
        const comptesData = response.data.data;
        compteOptions.value = comptesData.map((compte: any) => ({
          value: compte.id,
          label: `${typeCompteLabels[compte.typeCompte] || 'Type inconnu'} - ${compte.numero} - ${compte.intituleCompte}`,
        }));
      } catch (err) {
        error(err.message || 'Erreur lors de la récupération des comptes');
      }
    };

    const getCurrentDate = () => {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, "0");
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const year = today.getFullYear();
      return `${year}-${month}-${day}`;
    };

    const addDepot = async (values: any, { resetForm }: { resetForm: () => void }) => {
      try {
        if (values["montant"]){
          values["montant"]=parseInt(values["montant"].replace(/\s+/g, '').toString())
          const depotData = {
          ...values,
          compteEntreprise: showCompteField.value ? values.compteEntreprise : undefined,
          banque: showBanqueField.value ? values.banque : undefined,
          numeroCheque: showNumCompteField.value ? values.numeroCheque : undefined,
          dateVirement: showDateVirementField.value ? values.dateVirement : undefined,
         
        };
        const response = await ApiService.post("/depots", depotData);
        if (response.data.code === 201) {
          success(response.data.message);
          resetForm();
          router.push({ name: "ListeDepot" });
        }
            
        }
        
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de l'ajout du dépôt");
      }
    };

    onMounted(() => {
      getAllModesReglement();
      getAllComptes();
      getAllBanques();
      getAllCompteEntreprise();
    });

    return {
      depotSchema,
      addDepot,
      depotForm,
      getCurrentDate,
      getCurrentDateTime,
      onModeReglementSelect,
      modeReglementOptions,
      compteOptions,
      banqueOptions,
      compteEntrepriseOptions,
      modeReglement,
      compte,
      banque,
      numeroCheque,
      showCompteField,
      showBanqueField,
      showNumCompteField,
      showDateVirementField,
    };
  },
});
</script>